import dayjs from "dayjs";
import { PeriodResolution } from "../Metrics.types";
import { HydratedMetric, HydratedMetricValues } from "../HydratedMetric";
import { HydratedValue } from "../HydratedValue";
import { Period } from "../Period";
import { DeltaBase, DeltaResult } from "./DeltaBase";

export class WindowDelta extends DeltaBase {
    private sliceSize: number;

    constructor(
        metric: HydratedMetric,
        asOfDate: dayjs.Dayjs,
        sliceSize: number,
    ) {
        super(metric, asOfDate);
        this.sliceSize = sliceSize;
    }

    get windowSize(): number {
        return this.sliceSize * 2;
    }

    private generateExpectedPeriods(count: number): Period[] {
        const periods: Period[] = [];
        const resolution = this.metric.resolution;

        let latestPeriod: dayjs.Dayjs;
        if (resolution === PeriodResolution.MONTHLY) {
            latestPeriod = this.asOfDate.startOf("month");
        } else if (resolution === PeriodResolution.QUARTERLY) {
            latestPeriod = this.asOfDate.startOf("quarter");
            const isCompleteQuarter = this.asOfDate.isSame(
                this.asOfDate.endOf("quarter"),
                "day",
            );
            if (!isCompleteQuarter) {
                latestPeriod = latestPeriod.subtract(1, "quarter");
            }
        } else {
            latestPeriod = this.asOfDate.startOf("year");
        }

        for (let i = count - 1; i >= 0; i--) {
            let period: dayjs.Dayjs;
            if (resolution === PeriodResolution.MONTHLY) {
                period = latestPeriod.subtract(i, "month");
            } else if (resolution === PeriodResolution.QUARTERLY) {
                period = latestPeriod.subtract(i, "quarter");
            } else {
                period = latestPeriod.subtract(i, "year");
            }
            periods.push(new Period(period));
        }
        return periods;
    }

    private getPaddedValues(): HydratedMetricValues {
        const expectedPeriods = this.generateExpectedPeriods(this.windowSize);
        return this.getPaddedValuesForPeriods(expectedPeriods);
    }

    getAllValues(): HydratedMetricValues {
        const asOfPeriod = new Period(this.asOfDate);
        return [...this.metric.values]
            .filter(
                (v) =>
                    v.period.isBefore(asOfPeriod) ||
                    v.period.isSameAs(asOfPeriod),
            )
            .sort((a, b) =>
                a.period.isBefore(b.period)
                    ? -1
                    : a.period.isAfter(b.period)
                      ? 1
                      : 0,
            );
    }

    getMissingPeriods(): Period[] {
        return this.getPaddedValues()
            .filter((v) => v.value.isNull())
            .map((v) => v.period);
    }

    getCurrentSlice(): HydratedMetricValues {
        return this.getPaddedValues().slice(-this.sliceSize);
    }

    getPreviousSlice(): HydratedMetricValues {
        return this.getPaddedValues().slice(-this.windowSize, -this.sliceSize);
    }

    protected validate(): DeltaResult | null {
        if (this.sliceSize < 1) {
            return {
                value: new HydratedValue(null, this.metric.type),
                invalidWindowSize: true,
            };
        }
        return null;
    }

    getDeltaLabel(): string {
        if (
            this.sliceSize === 12 &&
            this.metric.resolution === PeriodResolution.MONTHLY
        )
            return "% Δ YoY";
        if (
            this.sliceSize === 4 &&
            this.metric.resolution === PeriodResolution.QUARTERLY
        )
            return "% Δ QoQ";
        if (
            this.sliceSize === 1 &&
            this.metric.resolution === PeriodResolution.YEARLY
        )
            return "% Δ YoY";
        return "% Δ PoP";
    }
}
